/**
 * AuditWidget - Reusable, embeddable audit flow component.
 *
 * Renders the complete OTP-gated audit journey inline:
 *   step1 (email + url) → otp (60s countdown) → loading → result (score + PDF CTA)
 *
 * Usage:
 *   <AuditWidget />                        - standalone card
 *   <AuditWidget compact />                - slim single-line form for hero sections
 *   <AuditWidget initialUrl="https://..." /> - pre-fill domain
 *   <AuditWidget initialEmail="..." />      - pre-fill email
 *
 * All audit entry points on the site should use this component or link to
 * /free-ai-growth-audit/ which renders the full AuditPage (which also uses
 * this same flow logic).
 */

import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import {
  auditWebsiteUrlSchema,
  normalizeAuditWebsiteUrl,
} from "@/lib/auditUrl";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import ScoreHumanCheck from "@/components/ScoreHumanCheck";
import {
  trackVerifiedScoreReportIssued,
  trackFormSubmit,
  trackFormOtpVerified,
  trackFormSuccess,
  trackFormError,
  trackOpenAiAuditPdfDelivery,
  trackScoreEvent,
} from "@/lib/analytics";
import AuditProgressBar, {
  type AuditStep,
} from "@/components/AuditProgressBar";
import AuditGenerationFeedback from "@/components/AuditGenerationFeedback";
import {
  AUDIT_OTP_EXPIRY_SECONDS,
  AUDIT_OTP_RESEND_COOLDOWN_SECONDS,
  formatAuditOtpCountdown,
  normaliseAuditOtpCode,
} from "@shared/auditOtpPolicy";

// ─── Schemas ──────────────────────────────────────────────────────────────────

const formSchema = z.object({
  url: auditWebsiteUrlSchema,
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Enter a valid work email"),
  marketingOptIn: z.boolean().optional(),
  company_website: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

// ─── Types ────────────────────────────────────────────────────────────────────

type WidgetState = "form" | "otp" | "loading" | "result" | "error";

// ─── Step progress config ─────────────────────────────────────────────────────

const WIDGET_STEPS: AuditStep[] = [
  { id: "form", label: "Your details" },
  { id: "otp", label: "Verify email" },
  { id: "loading", label: "Running audit" },
];

// result = all steps complete (sentinel not in array)
function widgetProgressStep(state: WidgetState): string {
  if (state === "result") return "__done__";
  return state;
}

export interface AuditWidgetCopy {
  intro?: string;
  priorityBenefit?: string;
  result?: string;
  generationPdf?: string;
}

interface Props {
  /** Optional presentation-only copy; default journeys remain unchanged. */
  copy?: AuditWidgetCopy;
  compact?: boolean;
  initialUrl?: string;
  initialEmail?: string;
  /** Called when the audit completes - useful for parent page analytics */
  onComplete?: (score: number) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type Rating =
  | "Critical"
  | "High Risk"
  | "Weak"
  | "Moderate"
  | "Strong"
  | "Excellent";

function ratingColor(rating: Rating): string {
  const map: Record<Rating, string> = {
    Critical: "#FF4D4D",
    "High Risk": "#FF6B35",
    Weak: "#FFB800",
    Moderate: "#FFD166",
    Strong: "#00C9A7",
    Excellent: "#06D6A0",
  };
  return map[rating] ?? "#8899A6";
}

function scoreGaugeColor(score: number): string {
  if (score < 40) return "#FF4D4D";
  if (score < 60) return "#FF6B35";
  if (score < 70) return "#FFB800";
  if (score < 80) return "#FFD166";
  if (score < 90) return "#00C9A7";
  return "#06D6A0";
}

function scoreLabel(score: number): string {
  if (score < 40) return "Critical";
  if (score < 60) return "High Risk";
  if (score < 70) return "Weak";
  if (score < 80) return "Moderate";
  if (score < 90) return "Strong";
  return "Excellent";
}

// ─── Mini score gauge ─────────────────────────────────────────────────────────

function MiniGauge({ score }: { score: number }) {
  const color = scoreGaugeColor(score);
  const label = scoreLabel(score);
  const r = 44;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="flex flex-col items-center">
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle
          cx="55"
          cy="55"
          r={r}
          fill="none"
          stroke="#1A2332"
          strokeWidth="10"
        />
        <circle
          cx="55"
          cy="55"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeDashoffset={circ / 4}
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${color}88)` }}
        />
        <text
          x="55"
          y="50"
          textAnchor="middle"
          fill={color}
          fontSize="26"
          fontWeight="900"
          fontFamily="sans-serif"
        >
          {score}
        </text>
        <text
          x="55"
          y="64"
          textAnchor="middle"
          fill="#8899A6"
          fontSize="10"
          fontFamily="sans-serif"
        >
          / 100
        </text>
      </svg>
      <span
        className="text-xs font-black tracking-widest px-3 py-0.5 rounded-full mt-1"
        style={{
          background: `${color}22`,
          color,
          border: `1px solid ${color}44`,
        }}
      >
        {label.toUpperCase()}
      </span>
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner({ color = "#FF8C00" }: { color?: string }) {
  return (
    <svg
      className="w-5 h-5 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke={color}
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill={color}
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ─── OTP countdown ring ───────────────────────────────────────────────────────

function OtpCountdownRing({
  seconds,
  total = 60,
}: {
  seconds: number;
  total?: number;
}) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const pct = seconds / total;
  const dash = pct * circ;
  const color =
    seconds > 120 ? "#00C9A7" : seconds > 60 ? "#FFB800" : "#FF4D4D";
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" className="mx-auto">
      <circle
        cx="36"
        cy="36"
        r={r}
        fill="none"
        stroke="#1A2332"
        strokeWidth="6"
      />
      <circle
        cx="36"
        cy="36"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={circ / 4}
        strokeLinecap="round"
        style={{
          transition: "stroke-dasharray 1s linear, stroke 0.3s ease",
          filter: `drop-shadow(0 0 4px ${color}88)`,
        }}
      />
      <text
        x="36"
        y="40"
        textAnchor="middle"
        fill={color}
        fontSize="18"
        fontWeight="900"
        fontFamily="sans-serif"
      >
        {formatAuditOtpCountdown(seconds)}
      </text>
    </svg>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AuditWidget({
  compact = false,
  initialUrl = "",
  initialEmail = "",
  onComplete,
  copy,
}: Props) {
  const [widgetState, setWidgetState] = useState<WidgetState>("form");
  const [pendingUrl, setPendingUrl] = useState(initialUrl);
  const [leadId, setLeadId] = useState<number | null>(null);
  const [auditResult, setAuditResult] = useState<any>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [resultToken, setResultToken] = useState<string | null>(null);
  const [pdfExpired, setPdfExpired] = useState(false);
  const [emailDelivered, setEmailDelivered] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // OTP state
  const [otpCode, setOtpCode] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpName, setOtpName] = useState("");
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(
    AUDIT_OTP_EXPIRY_SECONDS
  );
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [otpExpired, setOtpExpired] = useState(false);
  const otpTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const otpVerificationStartedRef = useRef(false);

  // PDF countdown (30 min)
  const [pdfSecondsLeft, setPdfSecondsLeft] = useState<number | null>(null);
  const pdfTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const openAiAuditConversionLeadIdRef = useRef<number | null>(null);
  const scoreLeadTrackedRef = useRef<number | null>(null);

  // Stored form values
  const [pendingForm, setPendingForm] = useState<FormValues | null>(null);
  const [otpToken, setOtpToken] = useState("");
  const [humanToken, setHumanToken] = useState("");

  // ── Form ──────────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { url: initialUrl, email: initialEmail },
  });

  // Pre-fill from query params if on the audit page
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const qEmail = params.get("email");
    const qUrl = params.get("url");
    if (qUrl) {
      const normalized = normalizeAuditWebsiteUrl(qUrl);
      if (normalized.ok) {
        setValue("url", normalized.value, { shouldValidate: false });
        setPendingUrl(normalized.value);
      }
    }
    if (qEmail) setValue("email", qEmail, { shouldValidate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── tRPC mutations ────────────────────────────────────────────────────────

  const sendOtpMutation = trpc.otp.send.useMutation({
    onSuccess: () => {
      trackScoreEvent("score_code_sent", {
        source_page: window.location.pathname,
      });
      setWidgetState("otp");
      startOtpCountdown();
    },
    onError: err => {
      toast.error(
        err.message || "Failed to send verification code. Please try again."
      );
      trackFormError({
        form_name: "audit_widget",
        form_location: "audit_widget",
        error_message: err.message,
      });
    },
  });

  const verifyOtpMutation = trpc.otp.verify.useMutation({
    onSuccess: data => {
      setOtpToken(data.token);
      trackFormOtpVerified({
        form_name: "audit_widget",
        form_location: "audit_widget",
      });
      trackScoreEvent("score_verified", {
        source_page: window.location.pathname,
      });
      if (pendingForm && leadId !== null) {
        submitMutation.mutate({
          email: pendingForm.email,
          leadId,
          otpToken: data.token,
        });
      }
    },
    onError: err => {
      toast.error(
        err.message || "Invalid code. Please check your email and try again."
      );
      setOtpCode("");
      otpVerificationStartedRef.current = false;
    },
  });

  const submitMutation = trpc.audit.submit.useMutation({
    onSuccess: data => {
      setLeadId(data.leadId);
      setResultToken(data.resultToken);
      setWidgetState("loading");
    },
    onError: err => {
      if (err.data?.code === "TOO_MANY_REQUESTS") {
        setErrorMsg(err.message);
        // The OTP has already been consumed. Show the actionable limit message
        // instead of leaving the verified form state without a running audit.
        setWidgetState("error");
      } else {
        setErrorMsg(err.message || "Something went wrong. Please try again.");
        setWidgetState("error");
      }
    },
  });

  const captureLeadMutation = trpc.audit.captureLead.useMutation({
    onSuccess: data => {
      if (data.ignored) return;
      if (!data.leadId || !pendingForm) {
        setErrorMsg("We could not save your score request. Please try again.");
        return;
      }
      setLeadId(data.leadId);
      trackScoreEvent("score_lead_saved", {
        source_page: window.location.pathname,
      });
      sendOtpMutation.mutate({
        email: pendingForm.email,
        name: pendingForm.name,
      });
    },
    onError: err => {
      setErrorMsg(
        err.message || "We could not save your score request. Please try again."
      );
      trackFormError({
        form_name: "audit_widget",
        form_location: "audit_widget",
        error_message: err.message,
      });
    },
  });

  // The worker can finish the score before the email provider accepts the
  // report. Continue polling only until delivery is confirmed so a conversion
  // cannot be attributed to OTP verification, score completion, or a download.
  const shouldPollAuditStatus =
    (widgetState === "loading" ||
      (widgetState === "result" && !emailDelivered)) &&
    leadId !== null &&
    resultToken !== null;

  const statusQuery = trpc.audit.status.useQuery(
    { leadId: leadId!, resultToken: resultToken ?? "not-ready" },
    {
      enabled: shouldPollAuditStatus,
      refetchInterval: shouldPollAuditStatus ? 4000 : false,
      retry: false,
    }
  );

  useEffect(() => {
    if (!statusQuery.data) return;
    const { status } = statusQuery.data;
    if (status === "complete") {
      const result = (statusQuery.data as any).audit;
      const resolvedPdfUrl = (statusQuery.data as any).pdfUrl ?? null;
      const resolvedExpired = !!(statusQuery.data as any).pdfExpired;
      // Count only a successfully delivered PDF. Do not count OTP verification,
      // audit start, retries, a result without email delivery, or PDF downloads.
      const emailDelivered = !!(statusQuery.data as any).emailDelivered;
      setEmailDelivered(emailDelivered);
      if (leadId !== null && scoreLeadTrackedRef.current !== leadId) {
        trackVerifiedScoreReportIssued({ placement: "hero" });
        scoreLeadTrackedRef.current = leadId;
      }
      if (
        leadId !== null &&
        emailDelivered &&
        openAiAuditConversionLeadIdRef.current !== leadId
      ) {
        trackOpenAiAuditPdfDelivery();
        trackScoreEvent("score_pdf", { source_page: window.location.pathname });
        openAiAuditConversionLeadIdRef.current = leadId;
      }
      setAuditResult(result);
      setPdfUrl(resolvedPdfUrl);
      setPdfExpired(resolvedExpired);
      if (resolvedPdfUrl && !resolvedExpired) {
        const THIRTY_MIN = 30 * 60;
        setPdfSecondsLeft(THIRTY_MIN);
        if (pdfTimerRef.current) clearInterval(pdfTimerRef.current);
        pdfTimerRef.current = setInterval(() => {
          setPdfSecondsLeft(prev => {
            if (prev === null || prev <= 1) {
              clearInterval(pdfTimerRef.current!);
              setPdfExpired(true);
              setPdfUrl(null);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
      setWidgetState("result");
      trackFormSuccess({
        form_name: "audit_widget",
        form_location: "audit_widget",
      });
      if (onComplete && result?.overallScore != null)
        onComplete(result.overallScore);
    } else if (status === "failed") {
      const crawlUnavailable = (statusQuery.data as any).reason === "crawl_unavailable";
      setErrorMsg(crawlUnavailable
        ? "We could not read this website well enough to issue a score. No score was created. Please try again later or book a consultation."
        : "The audit encountered an error. Please try again.");
      setWidgetState("error");
    }
  }, [statusQuery.data, leadId, onComplete]);

  useEffect(() => {
    return () => {
      if (otpTimerRef.current) clearInterval(otpTimerRef.current);
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      if (pdfTimerRef.current) clearInterval(pdfTimerRef.current);
    };
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function startOtpCountdown() {
    setOtpSecondsLeft(AUDIT_OTP_EXPIRY_SECONDS);
    setResendSecondsLeft(AUDIT_OTP_RESEND_COOLDOWN_SECONDS);
    setOtpExpired(false);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    otpTimerRef.current = setInterval(() => {
      setOtpSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(otpTimerRef.current!);
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    resendTimerRef.current = setInterval(() => {
      setResendSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const onFormSubmit = (values: FormValues) => {
    if (!humanToken) {
      setErrorMsg("Please complete the security check before continuing.");
      return;
    }
    setPendingForm(values);
    setPendingUrl(values.url);
    setOtpEmail(values.email);
    setOtpName(values.name);
    trackFormSubmit({
      form_name: "audit_widget",
      form_location: "audit_widget",
    });
    trackScoreEvent("score_start", { source_page: window.location.pathname });
    captureLeadMutation.mutate({
      ...values,
      sourcePage: window.location.pathname,
      humanCheckToken: humanToken,
    });
  };

  const handleOtpChange = (value: string) => {
    const code = normaliseAuditOtpCode(value);
    setOtpCode(code);
    if (
      code.length === 6 &&
      widgetState === "otp" &&
      !otpExpired &&
      !verifyOtpMutation.isPending &&
      !otpVerificationStartedRef.current
    ) {
      otpVerificationStartedRef.current = true;
      verifyOtpMutation.mutate({ email: otpEmail, code });
    }
  };

  const handleResend = () => {
    if (resendSecondsLeft > 0 || !otpEmail) return;
    setOtpCode("");
    otpVerificationStartedRef.current = false;
    sendOtpMutation.mutate({ email: otpEmail, name: otpName });
  };

  const handleReset = () => {
    setWidgetState("form");
    setLeadId(null);
    setAuditResult(null);
    setPdfUrl(null);
    setEmailDelivered(false);
    setErrorMsg("");
    setOtpCode("");
    setOtpToken("");
    setPendingForm(null);
    setPendingUrl(initialUrl);
    setOtpExpired(false);
    setOtpSecondsLeft(AUDIT_OTP_EXPIRY_SECONDS);
    setResendSecondsLeft(0);
    otpVerificationStartedRef.current = false;
  };

  const handleWidgetProgressStepSelection = (stepId: string) => {
    if (stepId === "form") {
      setWidgetState("form");
    }
  };

  const formatPdfTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // ── Shared card wrapper ────────────────────────────────────────────────────

  const cardStyle: React.CSSProperties = {
    background: "#1A2332",
    border: "1px solid #2A3A4A",
    borderRadius: "1rem",
    padding: compact ? "1.5rem" : "2rem",
  };

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: FORM
  // ─────────────────────────────────────────────────────────────────────────
  if (widgetState === "form") {
    return (
      <div data-yandex-sensitive className="ym-hide-content ym-disable-keys ym-disable-submit" style={cardStyle}>
        {/* Progress bar */}
        {!compact && (
          <div className="mb-5">
            <AuditProgressBar
              steps={WIDGET_STEPS}
              currentStep={widgetProgressStep(widgetState)}
              onStepSelect={undefined}
            />
          </div>
        )}
        {/* Header */}
        <div className="mb-5">
          <span
            className="inline-block text-xs font-bold tracking-widest px-3 py-1 rounded-full mb-3"
            style={{ background: "#FF8C00", color: "#0F1419" }}
          >
            FREE AI VISIBILITY SCORE
          </span>
          <h3 className="text-lg font-bold" style={{ color: "#F7F5F2" }}>
            Get your AI Visibility Score
          </h3>
          <p className="text-sm mt-1" style={{ color: "#8899A6" }}>
            {copy?.intro ?? <>A high-level score across seven dimensions, followed by a
            client-safe PDF by email.</>}
          </p>
        </div>

        {/* Error banner (rate limit) */}
        {errorMsg && (
          <div
            className="rounded-lg px-4 py-3 mb-4 text-sm"
            style={{
              background: "#FF4D4D22",
              border: "1px solid #FF4D4D44",
              color: "#FF4D4D",
            }}
          >
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <input
            {...register("company_website")}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[10000px] h-px w-px overflow-hidden opacity-0"
          />
          {/* URL */}
          <div>
            <label
              htmlFor="widget-website"
              className="block text-xs font-bold tracking-widest mb-1"
              style={{ color: "#FF8C00" }}
            >
              Website URL *
            </label>
            <input
              id="widget-website"
              {...register("url")}
              type="url"
              placeholder="https://yourcompany.com"
              autoComplete="url"
              aria-describedby="widget-website-error"
              className="w-full px-4 py-3 rounded-lg text-sm outline-none focus:ring-2"
              style={{
                background: "#0F1419",
                border: errors.url ? "1px solid #FF4D4D" : "1px solid #2A3A4A",
                color: "#F7F5F2",
              }}
            />
            {errors.url && (
              <p
                id="widget-website-error"
                className="text-xs mt-1"
                role="alert"
                style={{ color: "#FF4D4D" }}
              >
                {errors.url.message}
              </p>
            )}
          </div>

          {/* Name + Email row */}
          <div
            className={
              compact
                ? "grid grid-cols-1 gap-4"
                : "grid grid-cols-1 sm:grid-cols-2 gap-4"
            }
          >
            <div>
              <label
                htmlFor="widget-name"
                className="block text-xs font-bold tracking-widest mb-1"
                style={{ color: "#8899A6" }}
              >
                Full name *
              </label>
              <input
                id="widget-name"
                {...register("name")}
                name="name"
                type="text"
                placeholder="Jane Smith"
                autoComplete="name"
                aria-describedby="widget-name-error"
                className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                style={{
                  background: "#0F1419",
                  border: errors.name
                    ? "1px solid #FF4D4D"
                    : "1px solid #2A3A4A",
                  color: "#F7F5F2",
                }}
              />
              {errors.name && (
                <p
                  id="widget-name-error"
                  className="text-xs mt-1"
                  role="alert"
                  style={{ color: "#FF4D4D" }}
                >
                  {errors.name.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="widget-email"
                className="block text-xs font-bold tracking-widest mb-1"
                style={{ color: "#8899A6" }}
              >
                Work email *
              </label>
              <input
                id="widget-email"
                {...register("email")}
                name="email"
                type="email"
                placeholder="jane@company.com"
                autoComplete="email"
                aria-describedby="widget-email-error"
                className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                style={{
                  background: "#0F1419",
                  border: errors.email
                    ? "1px solid #FF4D4D"
                    : "1px solid #2A3A4A",
                  color: "#F7F5F2",
                }}
              />
              {errors.email && (
                <p
                  id="widget-email-error"
                  className="text-xs mt-1"
                  role="alert"
                  style={{ color: "#FF4D4D" }}
                >
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          {/* PECR opt-in */}
          {!compact && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register("marketingOptIn")}
                className="mt-0.5 rounded"
                style={{ accentColor: "#00C9A7", flexShrink: 0 }}
              />
              <span className="text-xs" style={{ color: "#8899A6" }}>
                Send me occasional AI visibility insights from
                Integrated.Social. Unsubscribe any time.
              </span>
            </label>
          )}

          <ScoreHumanCheck
            onToken={setHumanToken}
            disabled={
              captureLeadMutation.isPending || sendOtpMutation.isPending
            }
          />

          {/* Submit */}
          <button
            type="submit"
            disabled={
              isSubmitting ||
              captureLeadMutation.isPending ||
              sendOtpMutation.isPending ||
              !humanToken
            }
            className="w-full py-4 rounded-lg font-bold text-base transition-all duration-200 active:scale-95 disabled:opacity-60 flex items-center justify-center gap-3"
            style={{ background: "#FF8C00", color: "#0F1419" }}
          >
            {captureLeadMutation.isPending || sendOtpMutation.isPending ? (
              <>
                <Spinner color="#0F1419" /> Sending verification code...
              </>
            ) : (
              "Check my AI visibility"
            )}
          </button>

          <p className="text-xs text-center" style={{ color: "#556f85" }}>
            No credit card. Your score is unlocked after email verification.
          </p>
        </form>

        {/* What you get */}
        {!compact && (
          <div className="mt-6 pt-5" style={{ borderTop: "1px solid #2A3A4A" }}>
            <p
              className="text-xs font-bold tracking-widest mb-3"
              style={{ color: "#8899A6" }}
            >
              WHAT YOU RECEIVE
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: "⚡", text: "AI Visibility Score (0–100)" },
                { icon: "📊", text: "7-dimension scorecard with ratings" },
                { icon: "🔍", text: copy?.priorityBenefit ?? "Top 3 gaps costing you AI citations" },
                { icon: "📄", text: "Branded PDF report by email" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-sm flex-shrink-0">{item.icon}</span>
                  <span className="text-xs" style={{ color: "#8899A6" }}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: OTP
  // ─────────────────────────────────────────────────────────────────────────
  if (widgetState === "otp") {
    return (
      <div data-yandex-sensitive className="ym-hide-content ym-disable-keys ym-disable-submit" style={{ ...cardStyle, textAlign: "center" }}>
        {/* Progress bar */}
        {!compact && (
          <div className="mb-4 text-left">
            <AuditProgressBar
              steps={WIDGET_STEPS}
              currentStep={widgetProgressStep(widgetState)}
              onStepSelect={handleWidgetProgressStepSelection}
            />
          </div>
        )}
        {/* Countdown ring */}
        <OtpCountdownRing
          seconds={otpSecondsLeft}
          total={AUDIT_OTP_EXPIRY_SECONDS}
        />

        <h3
          className="text-lg font-bold mt-4 mb-2"
          style={{ color: "#F7F5F2" }}
        >
          {otpExpired ? "Code expired" : "Check your inbox"}
        </h3>
        <p className="text-sm mb-5" style={{ color: "#8899A6" }}>
          {otpExpired ? (
            "Your 15-minute code has expired. Request a new one below."
          ) : (
            <>
              We sent a 6-digit code to{" "}
              <strong style={{ color: "#F7F5F2" }}>{otpEmail}</strong>.<br />
              Enter it now - it expires in{" "}
              {formatAuditOtpCountdown(otpSecondsLeft)}.
            </>
          )}
        </p>

        {/* OTP input */}
        {!otpExpired && (
          <div className="flex justify-center mb-4">
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={handleOtpChange}
              pasteTransformer={normaliseAuditOtpCode}
              disabled={verifyOtpMutation.isPending || submitMutation.isPending}
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}

        {/* Verifying spinner */}
        {(verifyOtpMutation.isPending || submitMutation.isPending) && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <Spinner color="#00C9A7" />
            <span className="text-sm" style={{ color: "#00C9A7" }}>
              {verifyOtpMutation.isPending
                ? "Verifying code..."
                : "Starting your audit..."}
            </span>
          </div>
        )}

        {/* 15-minute entry window; the resend action is independently throttled. */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs" style={{ color: "#8899A6" }}>
            {resendSecondsLeft > 0
              ? `A new code can be requested in ${formatAuditOtpCountdown(resendSecondsLeft)}.`
              : "Check your spam folder, or request a new code."}
          </p>
          <button
            onClick={handleResend}
            disabled={sendOtpMutation.isPending || resendSecondsLeft > 0}
            className="flex items-center gap-2 mx-auto px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-95 disabled:opacity-60"
            style={{
              background: "#1A2332",
              border: "1px solid #FF8C00",
              color: "#FF8C00",
            }}
          >
            {sendOtpMutation.isPending ? (
              <>
                <Spinner color="#FF8C00" /> Sending...
              </>
            ) : resendSecondsLeft > 0 ? (
              `Resend available in ${formatAuditOtpCountdown(resendSecondsLeft)}`
            ) : (
              "Send a new code →"
            )}
          </button>
        </div>

        {/* Back link */}
        <button
          onClick={handleReset}
          className="mt-4 text-xs underline block mx-auto"
          style={{ color: "#8899A6" }}
        >
          ← Start over
        </button>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: LOADING
  // ─────────────────────────────────────────────────────────────────────────
  if (widgetState === "loading") {
    return (
      <div data-yandex-sensitive className="ym-hide-content ym-disable-keys ym-disable-submit" style={{ ...cardStyle, textAlign: "center" }}>
        <AuditGenerationFeedback mode="running" pdfStepCopy={copy?.generationPdf} />
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: RESULT
  // ─────────────────────────────────────────────────────────────────────────
  if (widgetState === "result" && auditResult) {
    const score: number = auditResult.overallScore ?? 0;
    const categories: any[] = auditResult.categories ?? [];

    return (
      <div data-yandex-sensitive className="ym-hide-content ym-disable-keys ym-disable-submit" style={cardStyle}>
        <AuditGenerationFeedback
          mode="complete"
          pdfStepCopy={copy?.generationPdf}
          email={otpEmail}
          pdfReady={Boolean(pdfUrl && !pdfExpired)}
          emailDelivered={emailDelivered}
          className="mb-5"
        />
        {/* Score header */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
          <MiniGauge score={score} />
          <div className="flex-1 text-center sm:text-left">
            <p
              className="text-xs font-bold tracking-widest mb-1"
              style={{ color: "#FF8C00" }}
            >
              YOUR AI VISIBILITY SCORE
            </p>
            <h3 className="text-xl font-bold mb-1" style={{ color: "#F7F5F2" }}>
              {pendingUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </h3>
            <p className="text-sm" style={{ color: "#8899A6" }}>
              {copy?.result ?? (score < 60
                ? "Significant gaps are costing you AI citations. Your competitors are being found instead."
                : score < 80
                  ? "Moderate visibility. There are clear opportunities to improve your AI citation rate."
                  : "Strong foundation. Fine-tuning will push you to the top of AI answers.")}
            </p>
          </div>
        </div>

        {/* Category bars */}
        {categories.length > 0 && (
          <div className="space-y-2 mb-5">
            {categories.slice(0, 4).map((cat: any, i: number) => {
              const color = ratingColor(cat.rating as Rating);
              return (
                <div
                  key={i}
                  className="rounded-lg p-3"
                  style={{
                    background: "#0F1419",
                    border: `1px solid ${color}33`,
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className="text-xs font-semibold"
                      style={{ color: "#F7F5F2" }}
                    >
                      {cat.category}
                    </span>
                    <span className="text-xs font-bold" style={{ color }}>
                      {cat.score}
                    </span>
                  </div>
                  <div
                    className="h-1.5 rounded-full"
                    style={{ background: "#1A2332" }}
                  >
                    <div
                      className="h-1.5 rounded-full"
                      style={{
                        width: `${cat.pct}%`,
                        background: color,
                        boxShadow: `0 0 4px ${color}66`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* PDF CTA */}
        {pdfUrl && !pdfExpired ? (
          <div
            className="rounded-xl p-4 mb-4"
            style={{ background: "#00C9A711", border: "1px solid #00C9A733" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p
                  className="text-xs font-bold tracking-widest mb-1"
                  style={{ color: "#00C9A7" }}
                >
                  PDF REPORT READY - EXPIRES IN{" "}
                  {pdfSecondsLeft !== null
                    ? formatPdfTime(pdfSecondsLeft)
                    : "30:00"}
                </p>
                <p className="text-xs" style={{ color: "#8899A6" }}>
                  Full report also sent to {otpEmail}
                </p>
              </div>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all duration-200 active:scale-95"
                style={{ background: "#00C9A7", color: "#0F1419" }}
              >
                Download PDF →
              </a>
            </div>
          </div>
        ) : pdfExpired ? (
          <div
            className="rounded-xl p-4 mb-4 text-xs"
            style={{
              background: "#FF4D4D11",
              border: "1px solid #FF4D4D33",
              color: "#FF4D4D",
            }}
          >
            PDF download link has expired. Check your email - the full report
            was sent to {otpEmail}.
          </div>
        ) : (
          <div
            className="rounded-xl p-4 mb-4 text-xs"
            style={{
              background: "#00C9A711",
              border: "1px solid #00C9A733",
              color: "#00C9A7",
            }}
          >
            Your full PDF report has been emailed to {otpEmail}.
          </div>
        )}

        {/* CTA */}
        <a
          aria-label="Book a consultation with Modi Elnadi"
          href="https://calendly.com/modielnadi/30min?utm_source=integrated.social&utm_medium=score-widget-result&utm_campaign=ai-visibility-score"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-3 rounded-lg font-bold text-sm text-center transition-all duration-200 active:scale-95 mb-3"
          style={{ background: "#FF8C00", color: "#0F1419" }}
        >
          Book a Consultation
        </a>
        <button
          onClick={handleReset}
          className="w-full py-2 rounded-lg text-xs font-semibold transition-all duration-200"
          style={{
            background: "transparent",
            border: "1px solid #2A3A4A",
            color: "#8899A6",
          }}
        >
          Run another audit
        </button>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: ERROR
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ ...cardStyle, textAlign: "center" }} role="alert">
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
        style={{
          background: "#FF4D4D22",
          color: "#FF4D4D",
          fontSize: "1.5rem",
        }}
      >
        ✕
      </div>
      <h3 className="text-lg font-bold mb-2" style={{ color: "#F7F5F2" }}>
        {errorMsg.includes("already used this free score")
          ? "Free score unavailable"
          : "Something went wrong"}
      </h3>
      <p className="text-sm mb-5" style={{ color: "#8899A6" }}>
        {errorMsg || "The audit encountered an error. Please try again."}
      </p>
      <button
        onClick={handleReset}
        className="px-6 py-3 rounded-lg font-bold text-sm transition-all duration-200 active:scale-95"
        style={{ background: "#FF8C00", color: "#0F1419" }}
      >
        {errorMsg.includes("already used this free score")
          ? "Return to score start"
          : "Try again →"}
      </button>
    </div>
  );
}
